"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";
import { ulid } from "ulid";
import { storage } from "~/components/utils/storage";
import { getClientAuthToken } from "~/utils/token";
import {
  create as createGraphql,
  type GraphQlApiClient,
} from "../graphql-client";
import { AppAPIRestClient } from "./rest";

export interface ApiConfig {
  name?: string;
  apiUrl: string;
  appApiUrl: string;
  apiKey: string;
  appleWebClientId?: string;
  appleDevTeamId?: string;
  iosAppId?: string;
  androidAppId?: string;
}

const restClientContext = createContext<AppAPIRestClient | null>(null);

const graphqlContext = createContext<GraphQlApiClient | null>(null);

export function APIClientProvider(props: {
  children: ReactNode;
  apiConfig: ApiConfig;
}) {
  const { apiConfig } = props;

  const { apiUrl, apiKey } = apiConfig;

  const restClient = useMemo(
    () => new AppAPIRestClient(apiUrl, apiKey),
    [apiUrl, apiKey],
  );

  const graphql = useMemo(
    () => createGraphql(apiConfig, getClientAuthToken),
    [apiConfig],
  );

  return (
    <restClientContext.Provider value={restClient}>
      <graphqlContext.Provider value={graphql}>
        {props.children}
      </graphqlContext.Provider>
    </restClientContext.Provider>
  );
}

export function useApiClient() {
  const apiClient = useContext(restClientContext);
  if (!apiClient) {
    throw new Error(
      "useApiClient must be used inside APIClientProvider component",
    );
  }
  return apiClient;
}

export function useGraphql() {
  const gqlClient = useContext(graphqlContext);
  if (!gqlClient) {
    throw new Error(
      "useGraphql must be used inside APIClientProvider component",
    );
  }
  return gqlClient;
}

/**
 * Used to identify the user agent, this can easily be reset by the user
 * so it doesn't provide security in that regard, however it is hard to guess
 * someone elses deviceId, use to identify returning users. Also can pin feature
 * flags to a device id
 */
export function getDeviceId(): string {
  if (typeof window === "undefined")
    throw new Error("device can not be used server side");

  const ls = storage();

  let deviceId = ls.getItem("device_id");

  if (!deviceId) {
    deviceId = ulid();
    ls.setItem("device_id", deviceId);
  }

  return deviceId;
}
